<template>
  <div class="nx-upload">
    <van-uploader
      v-model="fileList"
      :accept="accept"
      :before-read="beforeRead"
      :after-read="afterRead"
      :before-delete="handleRemove"
      :disabled="disabled"
      :deletable="false"
      :max-count="multiple ? maxCount : 1"
    >

      <div v-if="type === 'image'" class="upload-button">
        <slot v-if="showSlot"></slot>
        <van-icon v-else name="plus" />
      </div>
      <div v-else-if="type === 'video'" class="upload-button video"></div>

      <template #preview-cover="{url, content, status}">
        <img
          v-if="type === 'video'"
          :id="url"
          :key="url"
          class="video-preview"
          :src="url + '?x-oss-process=video/snapshot,t_1,m_fast,w_80,h_80'"
          alt=""
          @click="showVideoOverlay = true"
        >
        <van-image
          v-else-if="type === 'image'"
          :src="url || content"
          fit="cover"
          width="100%"
          height="100%"
        />
        <div v-if="status !== 'done'" class="van-uploader__mask">
          <div class="van-loading van-loading--circular van-uploader__loading">
            <span class="van-loading__spinner van-loading__spinner--circular">
              <svg viewBox="25 25 50 50" class="van-loading__circular">
                <circle cx="50" cy="50" r="20" fill="none"></circle>
              </svg>
            </span>
          </div>
          <div class="van-uploader__mask-message">上传中...</div>
        </div>
      </template>
    </van-uploader>
    <div v-if="tip" class="upload-tip">{{ tip }}</div>
    <nx-video-preview
      v-model="showVideoOverlay"
      :videos="fileList.map(v => v.url)"
    />
  </div>
</template>

<script>
import OSS from 'ali-oss';
import { Toast } from 'vant';
import { post_customer } from '@/http';
import { isImage, isVideo } from '@/utils/validate';

// 上传类型
export const uploadType = [
  { label: 'image', value: 1 },
  { label: 'video', value: 2 },
];

/**
 * 图片上传前验证
 */
function checkImage(file) {
  if (!isImage(file.name)) {
    onError('选择的文件格式不正确，请重新上传!');
    return false;
  }
  return true;
}
/**
 * 视频上传前验证
 */
function checkVideo(file) {
  if (!isVideo(file.name)) {
    onError('选择的文件格式不正确，请重新上传!');
    return false;
  }
  return true;
}

function onError(msg) {
  Toast(msg || '文件上传失败，请检查网络连接后重试');
}
export default {
  name: 'NxUpload',
  props: {
    value: {
      type: [Array, String],
      required: true,
    },
    maxCount: {
      type: Number,
      default: 1,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    maxSize: {
      type: Number,
      default: 5, // 1 = 1M
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'image', // 'image', 'video'
    },
    isMd5: { // 是否md5重命名
      type: Boolean,
      default: false,
    },
    tip: String,
    // 自定义上传样式
    showSlot: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fileList: [],
      showVideoOverlay: false,
      uploadingTasks: {}, // 上传中的任务
      uploading: false,
    };
  },
  computed: {
    modelValue: {
      get() {
        return this.value;
      },
      set(newVal) {
        this.$emit('input', newVal);
      },
    },
    accept() {
      return {
        image: 'image/*',
        video: 'video/*',
      }[this.type];
    },
  },
  watch: {
    value: {
      async handler(newVal) {
        this.fileList.length = 0;
        if (newVal) {
          const _list = this.multiple ? newVal.map(v => ({
            url: v,
            deletable: true,
            status: 'done',
          })) : [{
            url: newVal,
            deletable: true,
            status: 'done',
          }];
          this.fileList.push(..._list);
        }
      },
      immediate: true,
    },
  },
  methods: {
    beforeRead(file) {
      if (this.maxSize && file.size > this.maxSize * 1024 * 1024) {
        onError(`文件大小不能超过${this.maxSize}M`);
        return false;
      }
      const map = {
        image: checkImage,
        video: checkVideo,
      };
      return map[this.type](file);
    },
    // 文件读取后
    async afterRead(req) {
      const { file } = req;
      const key = Symbol();
      this.uploadingTasks[key] = true;
      this.uploading = true;
      const { url } = await this.uploadFile(file);
      this.onSuccess(url);
      delete this.uploadingTasks[key];
      this.uploading = Object.values(this.uploadingTasks).some(v => v);
    },
    async uploadFile(file) {
      const { data: uploadConfig } = await this.getTempKey(file, this.type);
      const {
        ossPath,
        secretId: accessKeyId,
        secretKey: accessKeySecret,
        sessionToken: stsToken,
        bucketName: bucket,
        regionName: region,
      } = uploadConfig[0];

      const client = new OSS({
        region,
        accessKeyId,
        accessKeySecret,
        stsToken,
        bucket,
        authorizationV4: true,
      });

      return client.put(ossPath, file).then(res => {
        console.log('upload res', res);
        return res;
      });
    },
    onSuccess(accessURL) {
      if (this.multiple && this.modelValue.length < this.maxCount) {
        (this.modelValue).push(accessURL);
      }
      else {
        this.modelValue = accessURL;
      }
    },
    handleRemove(item) {
      if (this.multiple) {
        this.modelValue = (this.modelValue).filter((v) => v !== item.url);
      }
      else {
        this.modelValue = '';
      }
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
    getTempKey(file, type) {
      return post_customer({
        url: `/beijing-saas-alicloud/exterior/oss/getCommonBucketTmpKey`,
        data: {
          ossFiles: [
            {
              fileName: file.name,
              filePath: '1234',
              hashCode: '1234',
            },
          ],
          type: (uploadType.find((item) => item.label === type) || {}).value || 3,
        },
      });
    },
  },
};

</script>

<style lang="scss" scoped>
.nx-upload {
  align-items: center;
  .video-preview,
  .upload-button,
  ::v-deep .van-uploader__file,
  ::v-deep .van-uploader__preview-cover,
  ::v-deep .van-uploader__mask,
  ::v-deep .van-uploader__preview-image
  {
    min-height: 80px;
    min-width: 80px;
    width: 100%;
    height: 100%;
  }

  ::v-deep .van-uploader,
  ::v-deep .van-uploader__wrapper,
  ::v-deep .van-uploader__preview{
    width: 100%;
    height: 100%;
  }
  .video-preview {
    background-color: #f7f8fa;
    overflow: hidden;
  }

  .upload-button {
    text-align: center;
    display:table-cell;
    vertical-align: middle;
    background-color: #f7f8fa;
    font-size: 28px;
    color: #aaa;

    &.video {
      background-size: contain;
      background-image: url(https://cdn-zone.app-prod.bjev.com.cn/beijing-h5/images/icon-video.m5DV9S4U.webp);
    }
  }

  .nx-upload-con {
    justify-content: center;
    height: 100%;
    font-size: 14px;
    font-weight: 400;
    color: #999999;
    text-align: center;
  }
  .nx-upload-icon {
    margin: 0 auto 12px;
  }

  .upload-tip {
    color: #B2B8C6;
    font-size: 12px;
    line-height: 2em;
  }
}
</style>
